import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  NEW_CUSTOMER_CITY_CHANGED,
  NEW_CUSTOMER_EMPLOYEE_ID_CHANGED,
  NEW_CUSTOMER_LAST_NAME_CHANGED,
  NEW_CUSTOMER_NAME_CHANGED,
  NEW_CUSTOMER_PHONE_CHANGED,
  NEW_CUSTOMER_SECOND_PARENT_LAST_NAME_CHANGED,
  NEW_CUSTOMER_SECOND_PARENT_NAME_CHANGED,
  NEW_CUSTOMER_SECOND_PARENT_NUMBER_CHANGED,
  NEW_CUSTOMER_STATE_CHANGED,
  NEW_CUSTOMER_STREET_CHANGED,
  NEW_CUSTOMER_STREET2_CHANGED,
  NEW_CUSTOMER_ZIP_CODE_CHANGED,
  NEW_CUSTOMER_EMAIL_ERROR_CHANGED,
  NEW_CUSTOMER_EMAIL_CHANGED,
} from '../../../actions/types';
import {
  loadAddressByZipCode,
  newCustomerChanged,
  saveQuote,
  setCurrentQuoteStep,
  toggleSecondPetParentForm,
  toggleYourInfoScrollToTop,
  validateCustomerExists,
  validateDiamondUser,
  validateIfEmailExists,
} from '../../../actions';
import Shimmer from '../../common/Shimmer';
import './PetParentInfo.css';
import {
  formatDate,
  isValidDiamondStreet,
  removeEmojis,
  shouldHideEmployeeField,
  shouldHideEmployeeInfoBubble,
  testContainNumber,
  testOnlyNumber,
  validateEmail,
} from '../../../util';
import {
  QuotingSteps,
  SAVE_QUOTE_TYPE,
  TEN_DIGIT_NUMBER,
} from '../../../constants';
import plusIcon from '../../../assets/plus-circle-icon.svg';
import check from '../../../assets/purchase/check.svg';
import {
  usePrevious,
  useTranslations,
} from '../../../hooks';
import { FigoInput } from '../../common/FigoInput';
import { dataLayerBeginCheckout } from '../../../gtmDataLayer';

const TYPES = {
  employeeID: NEW_CUSTOMER_EMPLOYEE_ID_CHANGED,
  newCustomerEmail: NEW_CUSTOMER_EMAIL_CHANGED,
  petParenStreetAddress: NEW_CUSTOMER_STREET_CHANGED,
  petParenStreetAddress2: NEW_CUSTOMER_STREET2_CHANGED,
  petParentCity: NEW_CUSTOMER_CITY_CHANGED,
  petParentFirstName: NEW_CUSTOMER_NAME_CHANGED,
  petParentLastName: NEW_CUSTOMER_LAST_NAME_CHANGED,
  petParentPhoneNumber: NEW_CUSTOMER_PHONE_CHANGED,
  petParentState: NEW_CUSTOMER_STATE_CHANGED,
  petParentZipCode: NEW_CUSTOMER_ZIP_CODE_CHANGED,
  secondaryPetParentLastName: NEW_CUSTOMER_SECOND_PARENT_LAST_NAME_CHANGED,
  secondaryPetParentMobilePhone: NEW_CUSTOMER_SECOND_PARENT_NUMBER_CHANGED,
  secondaryPetParentName: NEW_CUSTOMER_SECOND_PARENT_NAME_CHANGED,
};

const CheckIcon = () => (
  <img alt="" className="Pet-parent-check-otp" src={check} />
);

const PetParentInfo = ({
  containerRef = null,
  containerSecondaryRef = null,
  focusInputWithError = false,
  sendCustomerLead = () => { },
  validatePetParentInfo,
}) => {
  const store = useSelector(({ quoting }) => (quoting), shallowEqual);
  const { t } = useTranslations('quoting');
  const {
    employeeIdHelpText,
    employerId,
    newCustomer,
    newCustomer: {
      canEditAddress,
      emailError,
    },
    sessionInformation: {
      isLoading,
      nopCommerceUser,
    },
    data: { stateAbrv },
    verificationCode: { verified: isOtpVerified },
  } = store;
  const [isMounted, setIsMounted] = useState(false);
  const [nameError, setNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [streetError, setStreetError] = useState('');
  const [cityError, setCityError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [secondParentNameError, setSecondParentNameError] = useState('');
  const [secondLastNameError, setSecondLastNameError] = useState('');
  const [secondParentPhoneError, setSecondParentPhoneError] = useState('');
  const [employeeError, setEmployeeError] = useState('');
  const [isFirstValidation, setIsFirstValidation] = useState(true);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const petParentNameRef = useRef();
  const titleRef = useRef();
  const emailRef = useRef(null);
  const firstInputWithErrorRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    // Move focus to top
    titleRef?.current?.focus();
  }, []);

  const isNopComercedDisabled = useMemo(() => {
    if (canEditAddress) {
      return false;
    }

    return nopCommerceUser.LoginSuccess
      && nopCommerceUser.CustomerNopCommerceId;
  }, [
    canEditAddress,
    nopCommerceUser.CustomerNopCommerceId,
    nopCommerceUser.LoginSuccess,
  ]);

  useEffect(() => {
    if (store.customerZipCode !== newCustomer.address.zipCode
      || !newCustomer.address.city) {
      dispatch(loadAddressByZipCode(store.customerZipCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, store.customerZipCode, newCustomer.address.zipCode]);

  useEffect(() => {
    if (store.scrollYourInfoToTop) {
      window.scrollTo(0, 0);
      dispatch(toggleYourInfoScrollToTop(false));
    }
  }, [store.scrollYourInfoToTop, dispatch]);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
      dispatch(setCurrentQuoteStep(QuotingSteps.YourInfo.value));
    }
  }, [isMounted, dispatch]);

  useEffect(() => {
    if (!nopCommerceUser.LoginSuccess || !isMounted) {
      return;
    }

    setNameError('');
    setLastNameError('');
    setStreetError('');
    setCityError('');
  }, [isMounted, nopCommerceUser]);

  useEffect(() => {
    dispatch(newCustomerChanged({
      type: NEW_CUSTOMER_EMAIL_ERROR_CHANGED,
      value: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValueChanged = (event) => {
    const { id, value } = event.target;
    const cleanValue = removeEmojis(value);
    const containsNumber = testContainNumber(cleanValue);

    if (TYPES[id] === NEW_CUSTOMER_CITY_CHANGED
      && containsNumber && cleanValue) {
      return;
    }

    dispatch(newCustomerChanged({ type: TYPES[id], value: cleanValue }));
  };

  const onInputChangeCallback = (callbackState) => (event) => {
    callbackState('');
    onValueChanged(event);
  };

  const onInputFocusEmail = useCallback(() => {
    dispatch(newCustomerChanged({
      type: NEW_CUSTOMER_EMAIL_ERROR_CHANGED,
      value: '',
    }));
  }, [dispatch]);

  const onRequiredInputBlur = (errorMessage, setErrorCallBack) => (event) => {
    const { id, value } = event.target;
    dispatch(newCustomerChanged({ type: TYPES[id], value: value.trim() }));
    setErrorCallBack(value.trim() ? '' : errorMessage);
  };

  const toggleSecondaryPetParentForm = () => {
    dispatch(toggleSecondPetParentForm(!store.secondParentFormVisible));
  };

  const getStreetError = useCallback((value) => {
    let error = value ? '' : t('petParent.streetAddressRequired');

    if (value && !isValidDiamondStreet(value)) {
      error = t('petParent.invalidStreetError');
    }

    return error;
  }, [t]);

  const onStreetInputBlur = useCallback((event) => {
    const { id, value } = event.target;

    setStreetError(getStreetError(value));
    dispatch(newCustomerChanged({ type: TYPES[id], value: value.trim() }));
  }, [dispatch, getStreetError]);

  const onStreetValueChanged = useCallback((event) => {
    const { id, value } = event.target;
    const cleanValue = removeEmojis(value);
    dispatch(newCustomerChanged({ type: TYPES[id], value: cleanValue }));

    if (isValidDiamondStreet(cleanValue)) {
      setStreetError('');
    }
  }, [dispatch]);

  const onSecondPhoneNumberBlur = (event) => {
    const { value } = event.target;
    const { secondParent } = newCustomer;
    const { lastName, name, phoneNumber } = secondParent;

    let error = '';

    if (!name.trim() || !lastName.trim() || !phoneNumber.trim()) {
      error = value ? '' : t('petParent.mobileRequired');
    }

    if (value.trim().length >= 1) {
      error = TEN_DIGIT_NUMBER.test(value)
        ? '' : t('petParent.invalidPhoneError');
    }

    setSecondParentPhoneError(error);

    if (!error
      && secondParent.name && !secondParentNameError
      && secondParent.lastName && !secondLastNameError) {
      sendCustomerLead();
    }
  };

  const getPhoneError = useCallback((value) => {
    let error = value ? '' : t('petParent.mobileRequired');

    if (value) {
      error = TEN_DIGIT_NUMBER.test(value)
        ? '' : t('petParent.invalidPhoneError');
    }

    return error;
  }, [t]);

  const handlePhoneNumberError = useCallback((value) => {
    setPhoneNumberError(getPhoneError(value));
  }, [getPhoneError]);

  const onPhoneNumberBlur = useCallback((event) => {
    const { value } = event.target;
    handlePhoneNumberError(value);
  }, [handlePhoneNumberError]);

  const onPhoneNumberChanged = useCallback((event) => {
    const { id, value } = event.target;
    const onlyNumbersValue = testOnlyNumber(value)
      ? value
      : value.replace(/\D/g, '');
    dispatch(newCustomerChanged({ type: TYPES[id], value: onlyNumbersValue }));
  }, [dispatch]);

  const onEmployeeIDBlur = (event) => {
    const { id, value } = event.target;
    const error = value ? '' : t('petParent.employeeRequired');
    setEmployeeError(error);
    dispatch(newCustomerChanged({ type: TYPES[id], value: value.trim() }));
  };

  const onEmployeeChanged = (event) => {
    const { id, value } = event.target;

    dispatch(newCustomerChanged({ type: TYPES[id], value }));
  };

  const onSecondStreetAddressBlur = useCallback((event) => {
    const { id, value } = event.target;
    dispatch(newCustomerChanged({ type: TYPES[id], value: value.trim() }));
  }, [dispatch]);

  const onSecondaryPetInputBlur = (
    errorMessage,
    inputErrorCallBack,
  ) => (event) => {
    const { id, value } = event.target;
    const { secondParent } = newCustomer;
    const { lastName, name, phoneNumber } = secondParent;

    let error = '';

    if (!name.trim() || !lastName.trim() || !phoneNumber.trim()) {
      error = value ? '' : errorMessage;
    }

    inputErrorCallBack(error);
    dispatch(newCustomerChanged({ type: TYPES[id], value: value.trim() }));
  };

  const prevEmail = usePrevious(newCustomer.email);

  const {
    customerValidationResponse,
    parameters,
    guidData,
    quoteSelectedValues,
    saveQuote: { quoteId },
  } = store;

  const {
    groupCode,
    groupCodeDscr,
    ebPetQuoteResponseList,
    zipCode,
  } = store?.data || {};

  const previousEmailValidated = usePrevious(customerValidationResponse.email);

  useEffect(() => {
    if (!isFirstValidation
      && previousEmailValidated !== customerValidationResponse.email) {
      const cloudClientId = customerValidationResponse?.id || 0;
      const { GuID, EmployerEnrollmentPeriodEBs } = guidData;
      dispatch(saveQuote({
        cloudClientId,
        ebPetQuoteResponseList,
        EffectiveDate:
          formatDate(EmployerEnrollmentPeriodEBs[0].PolicyEffectiveDate),
        email: newCustomer.email || parameters.email,
        firstName: newCustomer.name || '',
        groupCode,
        groupCodeDscr,
        GuID,
        lastName: newCustomer.lastName || '',
        origin: window.location.origin,
        quoteId,
        quoteSelectedValues,
        QuoteSubId: store.subId,
        quoteType: SAVE_QUOTE_TYPE.auto,
        zipCode,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    customerValidationResponse.email,
    previousEmailValidated,
    isFirstValidation,
  ]);

  const onBlurEmail = async (event) => {
    const { value } = event.target;
    let error = value ? '' : t('petParent.emailRequired');

    if (value) {
      error = validateEmail(value) ? '' : t('petParent.invalidEmailError');
    }

    if (!error) {
      if (isFirstValidation || (!isFirstValidation && prevEmail !== value)) {
        const { userAlreadyExists, ...restData } =
          await validateIfEmailExists(value);

        dispatch(validateDiamondUser({
          customerExistsData: restData,
          email: value,
          userAlreadyExists,
          validateCustomer: false,
        }));

        emailRef.current?.focus();
        setIsFirstValidation(false);
      }
    }

    dispatch(newCustomerChanged({
      type: NEW_CUSTOMER_EMAIL_ERROR_CHANGED,
      value: error,
    }));
  };

  function renderSecondaryPetParentToggle() {
    return (
      <div
        ref={containerSecondaryRef}
        className="Second-parent-button-span-container"
      >
        <button
          aria-controls="secondaryParentInfo"
          aria-expanded={store.secondParentFormVisible}
          className="Second-parent-button-container Like-span"
          id="secondaryPetParentButtonContainer"
          onClick={toggleSecondaryPetParentForm}
          type="button"
        >
          <img
            alt=""
            className="Second-parent-plus-icon"
            id="secondaryPetParentButtonPlusIcon"
            src={plusIcon}
          />

          <span
            className="Second-parent-button-title"
            id="secondaryPetParentButtonTitle"
          >
            {t('petParent.secondaryPetParent')}
          </span>
        </button>

        <span
          className="Second-parent-brief-description"
        >
          {t('petParent.secondaryDescription')}
        </span>
      </div>
    );
  }

  function validateFields() {
    const nameErrorLocal = newCustomer.name
      ? '' : t('petParent.firstNameRequired');
    setNameError(nameErrorLocal);

    const lastNameErrorLocal = newCustomer.lastName
      ? '' : t('petParent.lastNameRequired');
    setLastNameError(lastNameErrorLocal);

    const streetErrorLocal = getStreetError(newCustomer.address.street);
    setStreetError(streetErrorLocal);

    const cityErrorLocal = newCustomer.address.city
      ? '' : t('petParent.cityRequired');
    setCityError(cityErrorLocal);

    const phoneNumberErrorLocal = getPhoneError(newCustomer.phoneNumber);
    setPhoneNumberError(phoneNumberErrorLocal);

    const employeeErrorLocal = newCustomer.employeeID
      ? '' : t('petParent.employeeRequired');
    setEmployeeError(employeeErrorLocal);

    if (!nameErrorLocal && !lastNameErrorLocal && !streetErrorLocal
      && !cityErrorLocal && !phoneNumberErrorLocal && !employeeErrorLocal) {
      if (store.secondParentFormVisible) {
        const secondParentNameErrorLocal = newCustomer.secondParent.name
          ? '' : t('petParent.firstNameRequired');
        setSecondParentNameError(secondParentNameErrorLocal);

        const secondLastNameErrorLocal = newCustomer.secondParent.lastName
          ? '' : t('petParent.lastNameRequired');
        setSecondLastNameError(secondLastNameErrorLocal);

        const secondPhoneErrorLocal =
          getPhoneError(newCustomer.secondParent.phoneNumber);
        setSecondParentPhoneError(secondPhoneErrorLocal);

        if (!secondParentNameErrorLocal
          && !secondLastNameErrorLocal
          && !secondPhoneErrorLocal) {
          dispatch(validateCustomerExists(newCustomer.email));
        }
      } else {
        dispatch(validateCustomerExists(newCustomer.email));
      }
    }
  }

  useEffect(() => {
    if (validatePetParentInfo > 0) {
      validateFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatePetParentInfo]);

  useEffect(() => {
    if (focusInputWithError && firstInputWithErrorRef.current) {
      firstInputWithErrorRef.current.focus();
    }
  }, [focusInputWithError]);

  const setRef = (error) => (node) => {
    if (error && !firstInputWithErrorRef.current) {
      firstInputWithErrorRef.current = node;
    }
  };

  const setFirstNameRef = (error) => (node) => {
    petParentNameRef.current = node;
    setRef(error)(node);
  };

  const setEmailRef = (error) => (node) => {
    emailRef.current = node;
    setRef(error)(node);
  };

  useEffect(() => {
    if (shouldHideEmployeeField(employeeIdHelpText)) {
      dispatch(newCustomerChanged({ type: TYPES.employeeID, value: 'N/A' }));
    }
  }, [dispatch, employerId, employeeIdHelpText]);

  const handleFocusCapture = useCallback(() => {
    if (!checkoutStarted) {
      dataLayerBeginCheckout();
      setCheckoutStarted(true);
    }
  }, [checkoutStarted]);

  // clean the ref to be reasigned each render
  firstInputWithErrorRef.current = null;

  function renderSecondaryPetParentForm() {
    return (
      <div
        className={`Second-parent-form ${store.secondParentFormVisible
          ? '' : 'Gone'}`}
        id="secondaryParentInfo"
      >
        <div className="Pet-parent-inputs-row">
          <FigoInput
            ref={setRef(secondParentNameError)}
            aria-required
            autocomplete
            error={!!secondParentNameError}
            id="secondaryPetParentName"
            label={t('petParent.firstName')}
            message={secondParentNameError}
            name="secondaryParentName"
            onBlur={onSecondaryPetInputBlur(
              t('petParent.firstNameRequired'),
              setSecondParentNameError,
            )}
            onChange={onInputChangeCallback(setSecondParentNameError)}
            placeholder={t('petParent.required', {
              field: t('petParent.firstName'),
            })}
            type="text"
            value={newCustomer.secondParent.name}
          />

          <FigoInput
            ref={setRef(secondLastNameError)}
            aria-required
            autocomplete
            error={!!secondLastNameError}
            id="secondaryPetParentLastName"
            label={t('petParent.lastName')}
            message={secondLastNameError}
            name="secondaryParentLastName"
            onBlur={onSecondaryPetInputBlur(
              t('petParent.lastNameRequired'),
              setSecondLastNameError,
            )}
            onChange={onInputChangeCallback(setSecondLastNameError)}
            placeholder={t('petParent.required', {
              field: t('petParent.lastName'),
            })}
            type="text"
            value={newCustomer.secondParent.lastName}
          />
        </div>

        <div className="Pet-parent-inputs-row">
          <FigoInput
            ref={setRef(secondParentPhoneError)}
            aria-required
            autocomplete
            containerClassName="Pet-parent-input-size"
            error={!!secondParentPhoneError}
            id="secondaryPetParentMobilePhone"
            label={t('petParent.mobilePhone')}
            maxLength={10}
            message={secondParentPhoneError}
            name="secondaryParentMobilePhone"
            onBlur={onSecondPhoneNumberBlur}
            onChange={onPhoneNumberChanged}
            placeholder={t('petParent.required', {
              field: t('petParent.mobilePhone'),
            })}
            type="text"
            value={newCustomer.secondParent.phoneNumber}
          />
        </div>
      </div>
    );
  }

  return (
    <section
      ref={containerRef}
      className="Pet-parent-container"
      id="petParentInfo"
    >
      <h1 ref={titleRef} className="Pet-parent-title" tabIndex={-1}>
        {t('petParent.title')}
      </h1>

      <form onFocusCapture={handleFocusCapture}>
        <div className="Pet-parent-inputs-row">
          <FigoInput
            ref={setFirstNameRef(nameError)}
            aria-required
            autocomplete
            error={!!nameError}
            id="petParentFirstName"
            label={t('petParent.firstName')}
            message={nameError}
            name="firstName"
            noBlankSpace
            onBlur={onRequiredInputBlur(
              t('petParent.firstNameRequired'),
              setNameError,
            )}
            onChange={onInputChangeCallback(setNameError)}
            placeholder={t('petParent.required', {
              field: t('petParent.firstName'),
            })}
            readOnly={isNopComercedDisabled}
            type="text"
            value={newCustomer.name}
          />

          <FigoInput
            ref={setRef(lastNameError)}
            aria-required
            autocomplete
            error={!!lastNameError}
            id="petParentLastName"
            label={t('petParent.lastName')}
            message={lastNameError}
            name="lastName"
            noBlankSpace
            onBlur={onRequiredInputBlur(
              t('petParent.lastNameRequired'),
              setLastNameError,
            )}
            onChange={onInputChangeCallback(setLastNameError)}
            placeholder={t('petParent.required', {
              field: t('petParent.lastName'),
            })}
            readOnly={isNopComercedDisabled}
            type="text"
            value={newCustomer.lastName}
          />
        </div>

        <div className="Pet-parent-inputs-row">
          <FigoInput
            ref={setRef(streetError)}
            aria-required
            autocomplete
            error={!!streetError}
            id="petParenStreetAddress"
            label={t('petParent.streetAddress')}
            message={streetError}
            name="streetAddress"
            noBlankSpace
            onBlur={onStreetInputBlur}
            onChange={onStreetValueChanged}
            placeholder={t('petParent.required', {
              field: t('petParent.streetAddress'),
            })}
            readOnly={isNopComercedDisabled}
            type="text"
            value={newCustomer.address.street}
          />

          <FigoInput
            autocomplete
            id="petParenStreetAddress2"
            label={t('petParent.streetAddress2')}
            name="streetAddress2"
            noBlankSpace
            onBlur={onSecondStreetAddressBlur}
            onChange={onValueChanged}
            placeholder={t('petParent.optionalPlaceholder', {
              field: t('petParent.streetAddress2'),
            })}
            readOnly={isNopComercedDisabled}
            type="text"
            value={newCustomer.address.street2}
          />
        </div>

        <div className="Pet-parent-inputs-row">
          <Shimmer
            className="Pet-parent-inputs-container"
            visible={store.isLoadingNewAddress}
            width={282}
          >
            <FigoInput
              ref={setRef(cityError)}
              aria-required
              autocomplete
              error={!!cityError}
              id="petParentCity"
              label={t('petParent.city')}
              message={cityError}
              name="city"
              noBlankSpace
              onBlur={onRequiredInputBlur(
                t('petParent.cityRequired'),
                setCityError,
              )}
              onChange={onInputChangeCallback(setCityError)}
              placeholder={t('petParent.cityPlaceholder')}
              readOnly={isNopComercedDisabled}
              type="text"
              value={newCustomer.address.city}
            />
          </Shimmer>

          <div className="Pet-parent-state-zip-inputs">
            <Shimmer
              className="Pet-parent-state-input"
              visible={store.isLoadingNewAddress}
            >
              <FigoInput
                autocomplete
                id="petParentState"
                label={t('petParent.state')}
                name="state"
                noBlankSpace
                readOnly
                type="text"
                value={newCustomer.address.state
                  || stateAbrv}
              />
            </Shimmer>

            <Shimmer
              className="Pet-parent-zip-input"
              visible={store.isLoadingNewAddress}
            >
              <FigoInput
                autocomplete
                id="petParentZipCode"
                label={t('petParent.zipCode')}
                name="zipCode"
                noBlankSpace
                readOnly
                type="text"
                value={newCustomer.address.zipCode}
              />
            </Shimmer>
          </div>
        </div>

        <div className="Pet-parent-inputs-row">
          <div className="Pet-parent-inputs-last-row">
            <FigoInput
              ref={setRef(phoneNumberError)}
              aria-required
              autocomplete
              error={!!phoneNumberError}
              id="petParentPhoneNumber"
              label={t('petParent.mobilePhone')}
              maxLength={10}
              message={phoneNumberError}
              name="phoneNumber"
              noBlankSpace
              onBlur={onPhoneNumberBlur}
              onChange={onInputChangeCallback(setPhoneNumberError)}
              placeholder={t('petParent.required', {
                field: t('petParent.mobilePhone'),
              })}
              type="text"
              value={newCustomer.phoneNumber}
            />

            {shouldHideEmployeeField(employeeIdHelpText) ? null : (
              <FigoInput
                ref={setRef(employeeError)}
                aria-required
                autocomplete
                error={!!employeeError}
                id="employeeID"
                label={t('petParent.employeeId')}
                maxLength={25}
                message={employeeError}
                onBlur={onEmployeeIDBlur}
                onChange={onEmployeeChanged}
                placeholder={t('petParent.required', {
                  field: t('petParent.employeeId'),
                })}
                title={t('petParent.employeeId')}
                tooltip={shouldHideEmployeeInfoBubble(employeeIdHelpText)
                  ? '' : store.employeeIdHelpText}
                value={newCustomer.employeeID}
              />
            )}
          </div>

          <FigoInput
            ref={setEmailRef(emailError)}
            aria-required
            autocomplete
            containerClassName={isOtpVerified && 'Pet-parent-email-verified'}
            error={!!emailError}
            hint={isOtpVerified
              ? t('login:otpFlow.emailVerified')
              : t('petParent.emailDescription')}
            id="newCustomerEmail"
            label={t('petParent.email')}
            message={emailError}
            name="email"
            noBlankSpace
            onBlur={onBlurEmail}
            onChange={onInputChangeCallback(onInputFocusEmail)}
            placeholder={t('petParent.required', {
              field: t('petParent.email'),
            })}
            readOnly={nopCommerceUser.LoginSuccess || isLoading}
            required
            rightIcon={isOtpVerified && <CheckIcon />}
            type="text"
            value={newCustomer.email}
          />
        </div>

        {renderSecondaryPetParentToggle()}

        {renderSecondaryPetParentForm()}
      </form>
    </section>
  );
};

export { PetParentInfo };
